import React from 'react';
import { Box, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { PageLayout } from '../components/layout';
import { useDiagnosis, useDiagnosisAll, useDiagnosisCustomer, useDiagnosisCustomerSites } from '../hooks';
import { useQuery } from 'react-query';
import { apiClient, axiosInstance } from '../services/apiClient';
import { UserReadDTO, DiagnosisReadDTO, DiagnosisEnergyUseReadDTO } from '../generated/api-client';
import { useAuth } from '../contexts/AuthContext';
import { DiagnosisTable, DiagnosisTableRow } from '../components/diagnosis/DiagnosisTable';
import {
  DiagnosisAdvancedFilter,
  DiagnosisAdvancedFilterState,
  EMPTY_ADVANCED_FILTER,
  applyDiagnosisAdvancedFilter,
} from '../components/diagnosis/DiagnosisAdvancedFilter';
import { HomeDiagnosisBarChartCard } from '../components/home/HomeDiagnosisBarChartCard';
import { HomeDiagnosisSummaryCard } from '../components/home/HomeDiagnosisSummaryCard';
import { HomeDiagnosisItalyMapCard } from '../components/home/HomeDiagnosisItalyMapCard';
import { dateLikeToIsoString, dateLikeToTimestamp } from '../utils/date.utils';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { useDeleteDiagnosis } from '../hooks/useDiagnosis';
import { isDiagnosisClosed } from '../utils/diagnosisStatus';
import { canEditDiagnosis } from '../utils/diagnosisPermissions';

const compareDiagnosesForHome = (a: DiagnosisReadDTO, b: DiagnosisReadDTO) => {
  const rank = (d: DiagnosisReadDTO) => {
    if (isDiagnosisClosed(d)) return 2;
    if (!d.diagnosis_date) return 0;
    return 1;
  };

  const rankA = rank(a);
  const rankB = rank(b);
  if (rankA !== rankB) return rankA - rankB;

  return dateLikeToTimestamp(b.diagnosis_date) - dateLikeToTimestamp(a.diagnosis_date);
};

const resolveAteco = (customer: {
  ateco_2025?: string | null;
  ateco_2022?: string | null;
  ateco_2007?: string | null;
}) => customer.ateco_2025 || customer.ateco_2022 || customer.ateco_2007 || '';

export const HomePage: React.FC = () => {
  const { user } = useAuth();
  const isAdminLike = user?.role === 'admin' || user?.role === 'mantainer';
  const [activeTab, setActiveTab] = React.useState<'mine' | 'all'>('mine');
  const [mineAdvancedFilter, setMineAdvancedFilter] =
    React.useState<DiagnosisAdvancedFilterState>(EMPTY_ADVANCED_FILTER);
  const [allAdvancedFilter, setAllAdvancedFilter] =
    React.useState<DiagnosisAdvancedFilterState>(EMPTY_ADVANCED_FILTER);
  const deleteMutation = useDeleteDiagnosis();
  const [deleteState, setDeleteState] = React.useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });

  const { data: mineDiagnoses = [], isLoading: isLoadingMine } = useDiagnosis();
  const { data: allDiagnoses = [], isLoading: isLoadingAll } = useDiagnosisAll();
  const rawDiagnosisList = activeTab === 'all' ? allDiagnoses : mineDiagnoses;
  const isLoading = activeTab === 'all' ? isLoadingAll : isLoadingMine;
  const advancedFilter = activeTab === 'all' ? allAdvancedFilter : mineAdvancedFilter;
  const setAdvancedFilter = activeTab === 'all' ? setAllAdvancedFilter : setMineAdvancedFilter;

  const { data: diagnosisSites = [] } = useDiagnosisCustomerSites();
  const { data: diagnosisCustomers = [] } = useDiagnosisCustomer();
  const { data: users = [] } = useQuery<UserReadDTO[]>('users', () =>
    apiClient.list_items_user__get()
  );
  const { data: allEnergyUses = [] } = useQuery<DiagnosisEnergyUseReadDTO[]>(
    ['diagnosis-energy-use', 'all'],
    async () => {
      const { data } = await axiosInstance.get<DiagnosisEnergyUseReadDTO[]>('/diagnosis-energy-use/');
      return data ?? [];
    }
  );

  const siteMap = React.useMemo(() => {
    const map = new Map<string, (typeof diagnosisSites)[number]>();
    diagnosisSites.forEach(site => site.id && map.set(site.id, site));
    return map;
  }, [diagnosisSites]);

  const customerMap = React.useMemo(() => {
    const map = new Map<string, (typeof diagnosisCustomers)[number]>();
    diagnosisCustomers.forEach(customer => customer.id && map.set(customer.id, customer));
    return map;
  }, [diagnosisCustomers]);

  const userMap = React.useMemo(() => {
    const map = new Map<string, UserReadDTO>();
    users.forEach(u => u.id && map.set(u.id, u));
    return map;
  }, [users]);

  const customerNameByDiagnosisId = React.useMemo(() => {
    const output: Record<string, string> = {};
    rawDiagnosisList.forEach(d => {
      if (!d.id) return;
      const site = d.site_id ? siteMap.get(d.site_id) : undefined;
      const customer = site?.customer_id ? customerMap.get(site.customer_id) : undefined;
      output[d.id] =
        [customer?.company_name, (customer as { company_name_type?: string })?.company_name_type]
          .filter(Boolean)
          .join(' ') || '—';
    });
    return output;
  }, [rawDiagnosisList, siteMap, customerMap]);

  const atecoByDiagnosisId = React.useMemo(() => {
    const output: Record<string, string> = {};
    rawDiagnosisList.forEach(d => {
      if (!d.id) return;
      const site = d.site_id ? siteMap.get(d.site_id) : undefined;
      const customer = site?.customer_id ? customerMap.get(site.customer_id) : undefined;
      output[d.id] = customer ? resolveAteco(customer) : '';
    });
    return output;
  }, [rawDiagnosisList, siteMap, customerMap]);

  const energyUsesByDiagnosisId = React.useMemo(() => {
    const output: Record<string, { code: string; description?: string }[]> = {};
    allEnergyUses.forEach(use => {
      if (!use.diagnosis_id || !use.code) return;
      if (!output[use.diagnosis_id]) output[use.diagnosis_id] = [];
      output[use.diagnosis_id].push({ code: use.code, description: use.description ?? undefined });
    });
    return output;
  }, [allEnergyUses]);

  const filteredDiagnoses = React.useMemo(
    () =>
      applyDiagnosisAdvancedFilter(
        rawDiagnosisList,
        advancedFilter,
        customerNameByDiagnosisId,
        atecoByDiagnosisId,
        energyUsesByDiagnosisId
      ),
    [
      rawDiagnosisList,
      advancedFilter,
      customerNameByDiagnosisId,
      atecoByDiagnosisId,
      energyUsesByDiagnosisId,
    ]
  );

  const baseDiagnoses = React.useMemo(
    () => [...filteredDiagnoses].sort(compareDiagnosesForHome),
    [filteredDiagnoses]
  );

  const tableRows = React.useMemo<DiagnosisTableRow[]>(() => {
    return baseDiagnoses.map(d => {
      const site = d.site_id ? siteMap.get(d.site_id) : undefined;
      const customer = site?.customer_id ? customerMap.get(site.customer_id) : undefined;
      const responsible = d.responsible_01 ? userMap.get(d.responsible_01) : undefined;
      const did = d.id ?? '';
      const progress = isDiagnosisClosed(d)
        ? 101
        : (d.checklist_progress_percent ?? 0);

      return {
        id: did,
        table_code_index: d.table_code_index,
        customer_name:
          [customer?.company_name, (customer as any)?.company_name_type].filter(Boolean).join(' ') || '—',
        site_address: site?.address ?? '—',
        responsible_01: responsible
          ? `${responsible.firstname ?? ''} ${responsible.lastname ?? ''}`.trim()
          : '—',
        last_update: dateLikeToIsoString(d.last_update),
        progress,
        _progressLoading: false,
        _canEdit: canEditDiagnosis(user, d),
      };
    });
  }, [baseDiagnoses, siteMap, customerMap, userMap, user]);

  const addressByDiagnosisId = React.useMemo(() => {
    const output: Record<string, string> = {};
    baseDiagnoses.forEach(d => {
      const address = d.site_id ? siteMap.get(d.site_id)?.address : undefined;
      if (d.id) output[d.id] = address ?? 'Indirizzo non disponibile';
    });
    return output;
  }, [baseDiagnoses, siteMap]);

  return (
    <PageLayout header={<Typography variant="h5">Benvenuto</Typography>}>
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              sx={{ minHeight: 40 }}
            >
              <Tab value="mine" label="Le mie diagnosi" />
              <Tab value="all" label="Tutte le diagnosi" />
            </Tabs>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Diagnosi energetiche
              {activeTab === 'mine' ? ' — Le mie' : ' — Tutte'}
            </Typography>
            <Stack spacing={2}>
              <DiagnosisAdvancedFilter
                diagnoses={rawDiagnosisList}
                customerNameByDiagnosisId={customerNameByDiagnosisId}
                atecoByDiagnosisId={atecoByDiagnosisId}
                energyUsesByDiagnosisId={energyUsesByDiagnosisId}
                value={advancedFilter}
                onChange={setAdvancedFilter}
              />
              <DiagnosisTable
                rows={tableRows}
                onRequestDelete={isAdminLike ? id => setDeleteState({ open: true, id }) : undefined}
                loading={isLoading}
                isFiltrable
                maxVisibleDataRows={8}
              />
            </Stack>
          </Paper>

          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
            <Box sx={{ flex: 2 }}>
              <HomeDiagnosisBarChartCard diagnoses={baseDiagnoses} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <HomeDiagnosisSummaryCard diagnoses={baseDiagnoses} currentUserId={user?.id} />
            </Box>
          </Stack>

          <HomeDiagnosisItalyMapCard
            diagnoses={baseDiagnoses}
            addressByDiagnosisId={addressByDiagnosisId}
          />
        </Stack>
      </Box>
      {isAdminLike && (
        <ConfirmDialog
          open={deleteState.open}
          title="Conferma eliminazione"
          content="Sei sicuro di voler eliminare questa diagnosi?"
          confirmText="Elimina"
          cancelText="Annulla"
          loading={deleteMutation.isLoading}
          onCancel={() => setDeleteState({ open: false, id: null })}
          onConfirm={() => {
            if (deleteState.id) {
              deleteMutation.mutate(deleteState.id, {
                onSettled: () => setDeleteState({ open: false, id: null }),
              });
            }
          }}
        />
      )}
    </PageLayout>
  );
};
